import type { QuoteSummaryTotalsView } from '../../utils/quoteSummaryLines';
import type { QuoteSummaryPaymentView } from '../../utils/quoteSummaryPayment';

interface Props {
    totals: QuoteSummaryTotalsView;
    payment: QuoteSummaryPaymentView;
}

const TERM = 'text-xs uppercase tracking-wide text-mp-muted';
const DETAIL = 'text-mp-foreground';
const LIST = 'grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:flex sm:flex-wrap sm:gap-8';

type SchedulePayment = Extract<QuoteSummaryPaymentView, { kind: 'schedule' }>;

function PaidInFull({ amount }: { amount: string }) {
    return (
        <p data-testid="summary-paid-in-full" className="text-sm text-mp-foreground">
            Full payment of <strong className="font-semibold">{amount}</strong>, collected in full at checkout.
        </p>
    );
}

function ScheduleDetails({ payment }: { payment: SchedulePayment }) {
    return (
        <dl className={LIST}>
            <div>
                <dt className={TERM}>Payment method</dt>
                <dd className={DETAIL} data-testid="summary-schedule-label">
                    {payment.methodLabel}
                </dd>
            </div>
            <div>
                <dt className={TERM}>Deposit</dt>
                <dd className={DETAIL} data-testid="summary-deposit">
                    {payment.deposit}
                </dd>
            </div>
            {payment.instalments.length > 0 && (
                <div>
                    <dt className={TERM}>Instalments</dt>
                    <dd className={DETAIL} data-testid="summary-instalments">
                        {payment.instalments.map((run, position) => (
                            <span key={position} className="block">
                                {run.label} <span>{run.amount}</span>
                            </span>
                        ))}
                    </dd>
                </div>
            )}
            <div>
                <dt className={TERM}>First collection</dt>
                <dd className={DETAIL} data-testid="summary-first-collection">
                    {payment.firstCollection}
                </dd>
            </div>
        </dl>
    );
}

export function QuoteSummaryHeader({ totals, payment }: Props) {
    return (
        <section aria-label="Quote total">
            <div
                className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between"
                data-testid="summary-totals-row"
            >
                <dl className={LIST}>
                    <div>
                        <dt className={TERM}>Total cost (inc VAT)</dt>
                        <dd
                            className="text-2xl font-semibold tabular-nums text-mp-foreground"
                            data-testid="summary-total"
                        >
                            {totals.total}
                        </dd>
                    </div>
                    <div>
                        <dt className={TERM}>Subtotal (ex VAT)</dt>
                        <dd className={DETAIL} data-testid="summary-subtotal">
                            {totals.subtotal}
                        </dd>
                    </div>
                    <div>
                        <dt className={TERM}>VAT</dt>
                        <dd className={DETAIL} data-testid="summary-vat">
                            {totals.vat}
                        </dd>
                    </div>
                </dl>
                <div className="border-t border-mp-border pt-4 lg:border-t-0 lg:pt-0">
                    {payment.kind === 'paid-in-full' ? (
                        <PaidInFull amount={payment.amount} />
                    ) : (
                        <ScheduleDetails payment={payment} />
                    )}
                </div>
            </div>
        </section>
    );
}
